import React, { useEffect, useState, useCallback, useMemo, lazy, Suspense } from "react";
import delete_Vector from "../../utils/delete_Vector.jpg";
import { AiFillDelete } from "react-icons/ai";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";
import filter from "../../utils/filter.png";
import { Link as RouterLink } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Outlet, useLocation } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { Button, ButtonGroup } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import KanbanViewQualified from "./KanbanQualified";
import QualifiedGrouped from "./QualifiedGrouped";
import LeadStageModal from "../../modals/LeadStageModal";
import LeadFilterModal from "../../modals/LeadFilterModal";
import { useBreadcrumbs } from "../../modals/BreadcrumbsContext";
import kanban from "../../utils/kanban.png";
import table from "../../utils/mainTable.svg";
import grouped from "../../utils/group.svg";
import products from "../../utils/products.png";
import expand from "../../utils/expand.png";
import axios from "axios";
import { format } from "date-fns";

// ✅ PERF FIX 1: Lazy-load Drawer — excluded from initial bundle, only loaded when first opened
const Drawer = lazy(() => import("./Drawer"));

// ─── Constants ────────────────────────────────────────────────────────────────


// ✅ PERF FIX 2: How many rows to show per page — change this to suit your UI
const PAGE_SIZE = 50;

// ─── Memoized Row Component ───────────────────────────────────────────────────
// ✅ PERF FIX 3: React.memo ensures only the row that changed re-renders
// when a checkbox is ticked — previously ALL 1000 rows re-rendered on every tick
const LeadRow = React.memo(({ row, isSelected, onCheck, onView, calculateAgedDays, stageMapping, products: productsIcon, expand: expandIcon }) => (
  <tr className={isSelected ? "bg-blue-100" : ""}>
    <td className="shadow-sm px-1 py-0 w-10">
      <div className="flex justify-right items-center gap-1.5">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onCheck(row.lead_id, row)}
        />
        {row?.Cnt > 0 && (
          <img src={productsIcon} alt="products-icon" className="w-1 h-1 bg-green-500 text-xs p-1 rounded-lg" />
        )}
      </div>
    </td>
    <td className="shadow-sm px-1 py-0.5 w-[380px]">
      <div className="flex items-end text-left justify-between w-auto">
        <div>{row.lead_organization}</div>
        <div className="flex items-center justify-center gap-2">
          <img
            onClick={() => onView(row.lead_id)}
            src={expandIcon}
            alt="expand-icon"
            className="w-4 h-4 cursor-pointer"
          />
          <div className="text-xs font-thin bg-gray-400 px-0.5 rounded-lg text-white">
            {calculateAgedDays(row.lead_entryTime)}
          </div>
        </div>
      </div>
    </td>
    <td className="shadow-sm px-1 py-0">
      {row.lead_stage && (
        <button
          className={`w-full text-white text-center rounded p-1 mb-0.5 ${
            row.lead_stage === "new"         ? "bg-yellow-500"  :
            row.lead_stage === "discovery"   ? "bg-teal-500"    :
            row.lead_stage === "proposal"    ? "bg-sky-500"     :
            row.lead_stage === "negotiation" ? "bg-orange-600"  :
            row.lead_stage === "won"         ? "bg-green-600"   :
            row.lead_stage === "lost"        ? "bg-red-600"     : "bg-gray-300"
          }`}
        >
          {stageMapping[row.lead_stage] || row.lead_stage}
        </button>
      )}
    </td>
    <td className="shadow-sm px-1 py-0">{row.lead_primarycontact}</td>
    <td className="shadow-sm px-1 py-0">{row.lead_primarycontacttitle}</td>
    <td className="shadow-sm px-1 py-0">{row.lead_email}</td>
    <td className="shadow-sm px-1 py-0">{row.lead_phone}</td>
    <td className="shadow-sm px-1 py-0">{row.lead_notes}</td>
    <td className="shadow-sm px-1 py-0 text-center">{row.lead_probability}</td>
  </tr>
));

// ─── Pagination Controls Component ───────────────────────────────────────────
const Pagination = React.memo(({ currentPage, totalPages, onPageChange, totalRecords, pageSize }) => {
  if (totalPages <= 1) return null;

  // Show at most 5 page buttons around the current page
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      range.push(i);
    }
    if (range[0] > 1) {
      if (range[0] > 2) range.unshift("...");
      range.unshift(1);
    }
    if (range[range.length - 1] < totalPages) {
      if (range[range.length - 1] < totalPages - 1) range.push("...");
      range.push(totalPages);
    }
    return range;
  };

  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord   = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className="flex items-center justify-between px-2 py-2 border-t border-gray-200 bg-white">
      {/* Record count */}
      <span className="text-xs text-gray-500">
        Showing <span className="font-medium">{startRecord}</span>–
        <span className="font-medium">{endRecord}</span> of{" "}
        <span className="font-medium">{totalRecords}</span> leads
      </span>

      {/* Page buttons */}
      <div className="flex items-center gap-1">
        {/* Prev */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          ‹
        </button>

        {getPageNumbers().map((page, idx) =>
          page === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-1 text-xs text-gray-400">…</span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-2 py-1 text-xs rounded border ${
                page === currentPage
                  ? "bg-blue-500 text-white border-blue-500"
                  : "border-gray-200 hover:bg-gray-100"
              }`}
            >
              {page}
            </button>
          )
        )}

        {/* Next */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          ›
        </button>
      </div>
    </div>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────────
const QualifiedLeads = () => {


  const company_code_from_local_storage = localStorage.getItem("companyCode") || "-";
const apiPaths_from_local_storage = localStorage.getItem("apiPaths")
  ? JSON.parse(localStorage.getItem("apiPaths"))
  : "-";
  
  const [isLoadingLeads, setIsLoadingLeads]                     = useState(false);
  const [freetext, setFreetext]                                 = useState("-");
  const [searchTerm, setSearchTerm]                             = useState("");
  const [leadsWithProducts, setLeadsWithProducts]               = useState(new Set());
  const [filteredQualifiedLeadRows, setFilteredQualifiedLeadRows] = useState([]);

  // ✅ PERF FIX 4: Use a Set instead of array for O(1) has/add/delete vs O(n) includes()
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [checkedLead, setCheckedLead]     = useState("");

  const [qualifiedLeads, setQualifiedLeads] = useState(
    () =>
      JSON.parse(localStorage.getItem("leadTableData"))?.map((lead) => ({
        ...lead,
        stage: lead.stage ?? "new",
      })) || []
  );

  // ✅ PERF FIX 5: Pagination state — reset to page 1 on new search/filter
  const [currentPage, setCurrentPage] = useState(1);

  const [discoveryLeads, setDiscoveryLeads] = useState(() => JSON.parse(localStorage.getItem("discoveryLeads")) || []);
  const [wonLeads, setWonLeads]             = useState(() => JSON.parse(localStorage.getItem("wonLeads"))       || []);
  const [lostLeads, setLostLeads]           = useState(() => JSON.parse(localStorage.getItem("lostLeads"))      || []);

  const [showStageModal, setShowStageModal]   = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDrawer, setShowDrawer]           = useState(false);
  const [selectedLead, setSelectedLead]       = useState(null);
  const [viewMode, setViewMode]               = useState("Grouped");
  const [history, setHistory]                 = useState([]);
  const [showLeadFilter, setShowLeadFilter]   = useState(false);
  const [rows, setRows]                       = useState([]);
  const [quoteLeadId, setQuoteLeadId]         = useState("");
  const [addLeadButton, setAddLeadButton]     = useState(true);

  const { showBreadcrumbs, setShowBreadcrumbs } = useBreadcrumbs();

  // ─── API Call ──────────────────────────────────────────────────────────────
  const callQualifiedLeadList = useCallback((userDepartment, searchText) => {
    let textToSearch = searchText && searchText.trim() !== "" ? searchText : "-";

    const currentUserRaw = localStorage.getItem("currentUser");
    if (!currentUserRaw || !apiPaths_from_local_storage) return;

    const currentUserObject          = JSON.parse(currentUserRaw);
    const empIDFromUserObjectInLocalStorage = currentUserObject[0]?.EMPID;
    const userAccessGroup            = currentUserObject[0]?.accessgrp;
    const USER_ACCESS                = currentUserObject[0]?.useraccess || "";

    const idOfSalesPersonToPassInAPI   = USER_ACCESS.indexOf("ZK") !== -1 ? empIDFromUserObjectInLocalStorage : "-";
    const idOfCoordinatorToPassInAPI   = USER_ACCESS.indexOf("ZL") !== -1 ? empIDFromUserObjectInLocalStorage : "-";
    const idOfCreatorViewToPassInAPI   = USER_ACCESS.indexOf("ZM") !== -1 ? empIDFromUserObjectInLocalStorage : "-";

    const todayDate = format(new Date(), "yyyy-MM-dd");
    const apiurl = `${apiPaths_from_local_storage[0].API_CONFIG}/api/CRMLeadMainList/${company_code_from_local_storage}/all/${idOfCoordinatorToPassInAPI}/${idOfSalesPersonToPassInAPI}/${idOfCreatorViewToPassInAPI}/-/${userDepartment}/1900-01-01/${todayDate}/${textToSearch}/qualified/-`;

    setIsLoadingLeads(true);
    axios
      .get(apiurl)
      .then((res) => {
        const updatedData = res.data.map((lead) => ({
          ...lead,
          lead_stage: lead.lead_stage ? lead.lead_stage : "new",
        }));
        setFilteredQualifiedLeadRows(updatedData);
        // ✅ Reset to page 1 whenever fresh data arrives
        setCurrentPage(1);
        // ✅ Clear selection when data refreshes
        setSelectedLeads(new Set());

        const leadsWithCount = new Set(
          res.data.filter((lead) => lead.Cnt > 0).map((lead) => lead.lead_id)
        );
        setLeadsWithProducts(leadsWithCount);
      })
      .catch((err) => console.error("Error fetching data:", err))
      .finally(() => setIsLoadingLeads(false));
  }, []);

  // ─── Search debounce ───────────────────────────────────────────────────────
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setFreetext(searchTerm.trim() === "" ? "-" : searchTerm);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    const userDepartment = localStorage.getItem("currentSelectedDepartment");
    callQualifiedLeadList(userDepartment, freetext);
  }, [freetext, callQualifiedLeadList]);

  // ─── Pagination derived data ───────────────────────────────────────────────
  // ✅ PERF FIX 6: Only slice the visible page — the rest of the data sits idle in state
  const totalPages  = Math.ceil(filteredQualifiedLeadRows.length / PAGE_SIZE);
  const pagedRows   = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredQualifiedLeadRows.slice(start, start + PAGE_SIZE);
  }, [filteredQualifiedLeadRows, currentPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    // Scroll table back to top when page changes
    document.getElementById("leads-table-wrapper")?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ─── stageMapping ──────────────────────────────────────────────────────────
  const stageMapping = useMemo(() => ({
    new:         "New",
    discovery:   "Discovery",
    proposal:    "Proposal",
    negotiation: "Negotiation",
    won:         "Won",
    lost:        "Lost",
  }), []);

  // ─── Checkbox handlers (Set-based) ────────────────────────────────────────
  const handleCheckboxChange = useCallback((id, row) => {
    setCheckedLead(row.lead);
    setSelectedLeads((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      // Select all records across ALL pages, not just current page
      setSelectedLeads(new Set(filteredQualifiedLeadRows.map((l) => l.lead_id)));
    } else {
      setSelectedLeads(new Set());
    }
  }, [filteredQualifiedLeadRows]);

  // ─── Delete ────────────────────────────────────────────────────────────────
  const deleteSelectedLeads = () => {
    const updatedLeads = qualifiedLeads.filter((lead) => !selectedLeads.has(lead.id));
    setQualifiedLeads(updatedLeads);
    setSelectedLeads(new Set());
    setShowDeleteModal(false);
  };

  const closeDeleteModal = () => setShowDeleteModal(false);

  // ─── Drawer open ──────────────────────────────────────────────────────────
  // ✅ PERF FIX 7: Find from full array once, then only pass the single lead object to Drawer
  const viewLead = useCallback((id) => {
    const lead = filteredQualifiedLeadRows.find((item) => item.lead_id === id);
    if (lead) {
      setSelectedLead(lead);
      requestAnimationFrame(() => setShowDrawer(true));
    }
  }, [filteredQualifiedLeadRows]);

  const handleInputChange = useCallback((e, field) => {
    setSelectedLead((prevLead) => ({ ...prevLead, [field]: e.target.value }));
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    const updatedLead    = { ...selectedLead };
    const originalLead   = qualifiedLeads.find((lead) => lead.id === selectedLead.id);
    let isChanged        = false;
    const updatedHistory = [...(updatedLead.history || [])];

    Object.keys(updatedLead).forEach((field) => {
      const oldValue = originalLead?.[field];
      const newValue = updatedLead[field];
      if (oldValue !== newValue) {
        updatedHistory.push({
          date:     new Date().toISOString(),
          activity: `Field "${field}" updated`,
          note:     `Changed from "${oldValue}" to "${newValue}"`,
        });
        isChanged = true;
      }
    });

    if (isChanged) updatedLead.history = updatedHistory;

    const updatedLeads = qualifiedLeads.map((lead) =>
      lead.id === selectedLead.id ? updatedLead : lead
    );
    setQualifiedLeads(updatedLeads);
    setShowDrawer(false);
  };

  // ─── Stage transitions ─────────────────────────────────────────────────────
  const closeStatusModal = () => setShowStageModal(false);

  const updateStageToWon = (leadId) => {
    const leadToMoveWon = rows.find((row) => row.lead_id === leadId);
    if (!leadToMoveWon) return;

    const currentTimestamp    = new Date().toISOString();
    const statusChangeTimeline = { date: currentTimestamp, activity: "Stage changed to Won" };

    const updatedQualifiedLeadWon = {
      ...leadToMoveWon,
      stage:    "won",
      timeline: Array.isArray(leadToMoveWon.timeline)
        ? [...leadToMoveWon.timeline, statusChangeTimeline]
        : [statusChangeTimeline],
    };

    setRows((prevRows) => prevRows.filter((row) => row.lead_id !== leadId));
    const updatedWonLeads = [...wonLeads, updatedQualifiedLeadWon];
    setWonLeads(updatedWonLeads);
    localStorage.setItem("wonLeads", JSON.stringify(updatedWonLeads));
    setShowStageModal(false);
  };

  const updateStageToLost = (leadId) => {
    const leadToMoveLost        = rows.find((row) => row.lead_id === leadId);
    if (!leadToMoveLost) return;

    const updatedQualifiedLeadLost = { ...leadToMoveLost, stage: "lost" };
    setRows((prevRows) => prevRows.filter((row) => row.lead_id !== leadId));
    const updatedLostLeads = [...lostLeads, updatedQualifiedLeadLost];
    setLostLeads(updatedLostLeads);
    localStorage.setItem("lostLeads", JSON.stringify(updatedLostLeads));
    setShowStageModal(false);
  };

  // ─── Misc helpers ──────────────────────────────────────────────────────────
  const calculateAgedDays = useCallback((leadEntryTime) => {
    if (!leadEntryTime) return "Aged Days: 0 d, 0 h";
    const diffInMilliseconds = new Date() - new Date(leadEntryTime);
    const diffInDays  = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor((diffInMilliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return `Aged Days: ${diffInDays} d, ${diffInHours} h`;
  }, []);

  const onLeadFilter   = () => setShowLeadFilter(true);
  const resetFilters   = () => { setFilteredQualifiedLeadRows(qualifiedLeads); setCurrentPage(1); };
  const applyFilters   = (filteredLeads) => { setFilteredQualifiedLeadRows(filteredLeads); setCurrentPage(1); };
  const handleSearch   = (term) => setSearchTerm(term);

  const updateLeadStage = (leadId, newStage) => {
    setFilteredQualifiedLeadRows((prevLeads) =>
      prevLeads.map((lead) => (lead.id === leadId ? { ...lead, stage: newStage } : lead))
    );
    closeStatusModal();
  };

  const navigate = useNavigate();

  const handleQuoteButtonClick = useCallback((leadId) => {
    const currentDate   = new Date();
    const formattedDate = `${currentDate.getDate().toString().padStart(2, "0")}/${(currentDate.getMonth() + 1)
      .toString().padStart(2, "0")}/${currentDate.getFullYear()} ${currentDate.getHours().toString().padStart(2, "0")}:${currentDate
      .getMinutes().toString().padStart(2, "0")}`;

    setQuoteLeadId(leadId);
    navigate(`/qualifiedLeadsTable/quoteLead/${leadId}`, { state: { formattedDate } });
    setShowDrawer(false);
    setShowBreadcrumbs(true);
  }, [navigate, setShowBreadcrumbs]);

  const handleBreadCrumbsClick = (event) => event.preventDefault();

  const location       = useLocation();
  const isQuotationRoute = location.pathname.includes("quoteLead");

  const theme = createTheme({
    typography: { fontFamily: `'Figtree', sans-serif` },
  });

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {isQuotationRoute ? (
        <Outlet />
      ) : (
        <div className="px-4 pb-0 pt-0 relative text-sm font-thin">
          {/* ── Header bar ── */}
          <div className="flex justify-between items-center mb-0">
            <div className="flex items-center justify-center gap-4">
              {!showBreadcrumbs && <h2 className="text-xl font-normal w-[150px]">Qualified Leads</h2>}

              {showBreadcrumbs && (
                <div role="presentation" onClick={handleBreadCrumbsClick}>
                  <div className="bg-gray-100 px-4 py-3 rounded-md shadow-md">
                    <ThemeProvider theme={theme}>
                      <Breadcrumbs aria-label="breadcrumb" className="flex items-center space-x-2 text-gray-600">
                        <Link
                          underline="hover"
                          color="inherit"
                          component={RouterLink}
                          to="/qualifiedLeadsTable"
                          className="flex items-center space-x-1 text-gray-700 hover:text-blue-600"
                        >
                          <span className="material-icons text-sm">home</span>
                          <h2 className="text-base font-medium truncate">Qualified Leads</h2>
                        </Link>
                        {showBreadcrumbs && quoteLeadId && (
                          <Link
                            underline="hover"
                            color="inherit"
                            component={RouterLink}
                            to={`/qualifiedLeadsTable/quoteLead/${quoteLeadId}`}
                            className="flex items-center space-x-1 text-gray-700 hover:text-blue-600"
                          >
                            <span className="material-icons text-sm">receipt</span>
                            <h2 className="text-base font-medium truncate">Quotation</h2>
                          </Link>
                        )}
                      </Breadcrumbs>
                    </ThemeProvider>
                  </div>
                </div>
              )}

              {addLeadButton && (
                <div className="flex gap-4 items-center">
                  <div className="relative flex justify-end my-2 w-[250px]">
                    <input
                      type="text"
                      className="py-2 pl-10 pr-4 rounded-lg text-black placeholder-gray-400 border border-none shadow-sm bg-white w-full"
                      placeholder="Search Qualified Leads..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <AiOutlineSearch className="text-gray-500" />
                    </div>
                    {searchTerm && (
                      <div
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                        onClick={() => handleSearch("")}
                      >
                        <AiOutlineClose className="text-gray-500" />
                      </div>
                    )}
                  </div>
                  <div>
                    <button
                      className="shadow-sm flex items-center space-x-2 px-2 py-1 border border-none rounded-full bg-white hover:bg-gray-100"
                      style={{ borderRadius: "50%", width: "30px", height: "30px", display: "flex", justifyContent: "center", alignItems: "center" }}
                      onClick={onLeadFilter}
                    >
                      <img src={filter} alt="filter-icon" className="w-6 h-6" style={{ width: "16px", objectFit: "contain" }} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* View mode toggle */}
            <div>
              <ButtonGroup
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  "& .MuiButton-outlined": { border: "none" },
                }}
              >
                <Button onClick={() => { setViewMode("Grouped"); setAddLeadButton(true); }} sx={{ backgroundColor: "transparent" }}>
                  <img src={grouped} alt="table-icon" className="w-6 h-6"
                    style={{ filter: viewMode === "Grouped" ? "invert(29%) sepia(98%) saturate(2942%) hue-rotate(202deg) brightness(95%) contrast(92%)" : "none" }} />
                </Button>
                <Button onClick={() => { setViewMode("Tabular"); setAddLeadButton(true); }} sx={{ backgroundColor: "transparent" }}>
                  <img src={table} alt="table-icon" className="w-6 h-6"
                    style={{ filter: viewMode === "Tabular" ? "invert(29%) sepia(98%) saturate(2942%) hue-rotate(202deg) brightness(95%) contrast(92%)" : "none" }} />
                </Button>
                <Button onClick={() => { setViewMode("Kanban"); setAddLeadButton(false); }} sx={{ backgroundColor: "transparent" }}>
                  <img src={kanban} alt="kanban-icon" className="w-6 h-6"
                    style={{ filter: viewMode === "Kanban" ? "invert(29%) sepia(98%) saturate(2942%) hue-rotate(202deg) brightness(95%) contrast(92%)" : "none" }} />
                </Button>
              </ButtonGroup>
            </div>
          </div>

          {/* ── Loading spinner ── */}
          {isLoadingLeads && (
            <div className="flex justify-center items-center py-20">
              <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* ── Empty state ── */}
          {!isLoadingLeads && filteredQualifiedLeadRows.length === 0 && (
            <div className="flex flex-col justify-center items-center py-20 gap-2 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-sm font-medium">No Data</p>
            </div>
          )}

          {/* ── Main content ── */}
          {!isLoadingLeads && filteredQualifiedLeadRows.length > 0 && (
            <>
              {viewMode === "Kanban" ? (
                <KanbanViewQualified rows={rows} setRows={setRows} />

              ) : viewMode === "Grouped" ? (
                <QualifiedGrouped
                  filteredQualifiedLeadRows={filteredQualifiedLeadRows}
                  stageMapping={stageMapping}
                  deleteSelectedLeads={deleteSelectedLeads}
                  showDeleteModal={showDeleteModal}
                  calculateAgedDays={calculateAgedDays}
                  selectedLeads={Array.from(selectedLeads)}
                  setSelectedLeads={(val) =>
                    setSelectedLeads(Array.isArray(val) ? new Set(val) : val)
                  }
                  closeDeleteModal={closeDeleteModal}
                  rows={rows}
                  setRows={setRows}
                  selectedLead={selectedLead}
                  history={history}
                />

              ) : (
                /* ── Tabular view ── */
                <div>
                  {selectedLeads.size > 0 && (
                    <div className="mb-2">
                      <button
                        onClick={() => setShowDeleteModal(true)}
                        className="bg-red-500 text-white px-2 py-2 mt-2 rounded mr-2"
                      >
                        <AiFillDelete size={18} />
                      </button>
                    </div>
                  )}

                  {/* ✅ Table: only PAGE_SIZE rows in the DOM at a time */}
                  <div id="leads-table-wrapper" className="overflow-x-auto mt-2 max-h-[70vh] overflow-y-auto scrollbar-thin">
                    <table className="table-auto w-[100%] border-collapse border border-gray-200">
                      <thead className="bg-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="shadow-sm px-1 py-0 text-left font-normal">
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                selectedLeads.size > 0 &&
                                selectedLeads.size === filteredQualifiedLeadRows.length
                              }
                            />
                          </th>
                          <th className="shadow-sm px-1 py-0 text-left w-[380px] font-normal">Organization/Customer</th>
                          <th className="shadow-sm px-1 py-0 text-left w-[100px] font-normal">Stage</th>
                          <th className="shadow-sm px-1 py-0.5 text-left w-[100px] font-normal">Contact Person</th>
                          <th className="shadow-sm px-1 py-0 text-left w-[150px] font-normal">Title</th>
                          <th className="shadow-sm px-1 py-0 text-left w-[150px] font-normal">Email</th>
                          <th className="shadow-sm px-1 py-0 text-left w-[100px] font-normal">Phone</th>
                          <th className="shadow-sm px-1 py-0 text-left w-[150px] font-normal">Notes</th>
                          <th className="shadow-sm px-1 py-0 text-left w-[50px] font-normal">Probability</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {/* ✅ pagedRows = only 50 rows, not 1000 */}
                        {pagedRows.map((row) => (
                          <LeadRow
                            key={row.lead_id}
                            row={row}
                            isSelected={selectedLeads.has(row.lead_id)}
                            onCheck={handleCheckboxChange}
                            onView={viewLead}
                            calculateAgedDays={calculateAgedDays}
                            stageMapping={stageMapping}
                            products={products}
                            expand={expand}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* ✅ Pagination bar */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalRecords={filteredQualifiedLeadRows.length}
                    pageSize={PAGE_SIZE}
                  />

                  {/* ✅ Drawer: lazy-loaded + only mounted when needed, receives single lead not full array */}
                  {showDrawer && selectedLead && (
                    <Suspense fallback={null}>
                      <Drawer
                        showDrawer={showDrawer}
                        setShowDrawer={setShowDrawer}
                        selectedLead={selectedLead}
                        handleInputChange={handleInputChange}
                        handleSave={handleSave}
                        rows={rows}
                        setRows={setRows}
                        leadsWithHistory={history}
                        stageMapping={stageMapping}
                        setShowBreadcrumbs={setShowBreadcrumbs}
                        handleQuoteButtonClick={handleQuoteButtonClick}
                        // ✅ REMOVED: filteredQualifiedLeadRows — was causing Drawer to
                        //    re-render with all 1000 leads on every open. Pass only what
                        //    the Drawer actually needs. If Drawer needs the list for a
                        //    specific sub-feature, pass a derived/filtered subset instead.
                      />
                    </Suspense>
                  )}

                  {/* Delete modal */}
                  {showDeleteModal && selectedLeads.size > 0 && (
                    <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50">
                      <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full text-center relative">
                        <div className="flex justify-center mb-2">
                          <img src={delete_Vector} alt="Delete Icon" className="w-1/2 h-auto max-w-xs" />
                        </div>
                        <button className="absolute top-6 right-8 text-gray-500 hover:text-black" onClick={closeDeleteModal}>✖</button>
                        <div className="text-xl font-semibold text-black">
                          {selectedLeads.size === 1 ? "Lead Selected" : "Leads Selected"}
                        </div>
                        <div className="text-gray-700 mb-6">
                          {selectedLeads.size === 1 ? (
                            <span className="font-bold">Are you sure you want to delete {checkedLead}?</span>
                          ) : (
                            <span className="font-bold">Are you sure you want to delete {selectedLeads.size} leads?</span>
                          )}
                        </div>
                        <div className="flex justify-center space-x-4">
                          <button className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600" onClick={deleteSelectedLeads}>Delete</button>
                          <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300" onClick={closeDeleteModal}>Cancel</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filter modal */}
                  {showLeadFilter && (
                    <LeadFilterModal
                      rows={qualifiedLeads}
                      resetFilters={resetFilters}
                      applyFilters={applyFilters}
                      isQualifiedLeads={true}
                      closeFilter={() => setShowLeadFilter(false)}
                    />
                  )}

                  {/* Stage modal */}
                  {showStageModal && selectedLead && (
                    <LeadStageModal
                      closeStatusModal={closeStatusModal}
                      updateStageToWon={updateStageToWon}
                      updateStageToLost={updateStageToLost}
                      selectedLead={selectedLead}
                      updateLeadStage={updateLeadStage}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default QualifiedLeads;